import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Phone2Icon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@newcore/ui/dialog';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { useQueryClient } from '@tanstack/react-query';
import {
  DevicePairingService,
  PairingSessionStatus,
} from '@/api/user-portal-api-generated';
import { queryKeys } from '@/common/queryClient';
import {
  PairingActiveState,
  PairingErrorState,
  PairingExpiredState,
  PairingIntroState,
  PairingLoadingState,
  PairingSuccessState,
} from '@/components/device-pairing/states';
import { DevicePairingStatus } from '@/enums/device-pairing-status';
import {
  getOnboardingDevicePairingStatus,
  startOnboardingDevicePairing,
} from '@/features/onboarding/api/user-onboarding-api';
import { buildPairingQrValue } from '@/utils/pairing-utils';

interface DevicePairingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cancelButtonText?: string;
  /** If true, uses onboarding endpoints (session cookie). If false, uses enrollment endpoints (authenticated user session). */
  isOnboarding?: boolean;
  /** If true, shows intro screen first before pairing. Used at end of onboarding flow. */
  showIntro?: boolean;
  /** Tenant slug used to build the QR pairing URL. Falls back to the `:tenant` route param. */
  tenantSlug?: string;
}

const POLLING_INTERVAL_MS = 2000;
// Tolerate client clock behind server and Redis TTL lag.
const EXPIRY_GRACE_MS = 30_000;

/**
 * Dialog for adding a new device via QR code pairing.
 *
 * Used in two contexts:
 * - Onboarding flow (isOnboarding=true): Uses onboarding session cookie for auth
 * - Enrollment flow (isOnboarding=false): Uses authenticated user session for auth
 */
export const DevicePairingDialog: React.FC<DevicePairingDialogProps> = ({
  isOpen,
  onClose,
  cancelButtonText = 'Cancel',
  isOnboarding = false,
  showIntro = false,
  tenantSlug,
}) => {
  const queryClient = useQueryClient();
  const { tenant: tenantFromRoute } = useParams<{ tenant: string }>();
  const tenant = tenantSlug ?? tenantFromRoute;
  const [pairingData, setPairingData] = useState<{
    pairingCode: string;
    expiresAt: Date;
  } | null>(null);
  // Start with INTRO status if showIntro is true, otherwise IDLE
  const [status, setStatus] = useState<DevicePairingStatus>(
    showIntro ? DevicePairingStatus.INTRO : DevicePairingStatus.IDLE
  );
  const [error, setError] = useState<string | null>(null);
  const [isPaired, setIsPaired] = useState(false);
  const hasStartedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Start pairing when dialog opens (skip if showing intro first)
  useEffect(() => {
    if (
      isOpen &&
      !hasStartedRef.current &&
      status !== DevicePairingStatus.INTRO
    ) {
      hasStartedRef.current = true;
      setStatus(DevicePairingStatus.STARTING);
      setError(null);

      const startPairing = async (): Promise<void> => {
        try {
          // Use onboarding endpoint (session cookie) or enrollment endpoint (authenticated user session)
          const response = isOnboarding
            ? await startOnboardingDevicePairing()
            : await DevicePairingService.startDevicePairing();
          setPairingData({
            pairingCode: response.pairing_code,
            expiresAt: new Date(response.expires_at),
          });
          setStatus(DevicePairingStatus.ACTIVE);
        } catch (err) {
          setStatus(DevicePairingStatus.ERROR);
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to start device pairing'
          );
        }
      };

      void startPairing();
    }
  }, [isOpen, isOnboarding, status]);

  // Start polling for pairing completion when we have pairing data
  // Use dedicated lightweight status endpoint (like enrollment flow)
  useEffect(() => {
    if (pairingData && status === DevicePairingStatus.ACTIVE && !isPaired) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(async () => {
        if (isPaired) {
          return;
        }

        try {
          const statusResponse = isOnboarding
            ? await getOnboardingDevicePairingStatus(pairingData.pairingCode)
            : await DevicePairingService.getPairingStatus(
                pairingData.pairingCode
              );

          if (statusResponse.status === PairingSessionStatus.COMPLETED) {
            setIsPaired(true);
            setStatus(DevicePairingStatus.SUCCESS);
            void queryClient.invalidateQueries({
              queryKey: queryKeys.devices.all(),
            });

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }

            // Close dialog after brief success display
            // Parent wizard will handle completion (calls /onboarding/complete)
            // Store timeout ref so it can be cleared if dialog closes manually
            autoCloseTimeoutRef.current = setTimeout(() => {
              autoCloseTimeoutRef.current = null;
              onClose();
            }, 500); // Small delay to show success state briefly
          } else if (
            statusResponse.status === PairingSessionStatus.EXPIRED ||
            statusResponse.status === PairingSessionStatus.CANCELLED
          ) {
            setStatus(DevicePairingStatus.EXPIRED);

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
          // PENDING - continue polling
        } catch {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Server deletes the code at expiry, so polls then fail with 404.
          if (Date.now() >= pairingData.expiresAt.getTime() - EXPIRY_GRACE_MS) {
            setStatus(DevicePairingStatus.EXPIRED);

            return;
          }

          onClose();
        }
      }, POLLING_INTERVAL_MS);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [pairingData, status, isPaired, onClose, isOnboarding, queryClient]);

  // Reset when dialog closes
  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
      /* eslint-disable react-hooks/set-state-in-effect -- reset on close */
      setIsPaired(false);
      setPairingData(null);
      // Reset to INTRO if showIntro is true, otherwise IDLE
      setStatus(
        showIntro ? DevicePairingStatus.INTRO : DevicePairingStatus.IDLE
      );
      setError(null);

      /* eslint-enable react-hooks/set-state-in-effect */
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Clear auto-close timeout to prevent double onClose calls
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    }
  }, [isOpen, showIntro]);

  // Note: Dialog auto-closes after pairing succeeds (with small delay to show success)
  // FactorSteps will automatically show alternatives screen when hasAlternative=true

  const handleRetry = (): void => {
    hasStartedRef.current = false;
    setStatus(DevicePairingStatus.IDLE);
    setError(null);
    setPairingData(null);
    setIsPaired(false);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle "Set up Core Mobile App" from intro screen
  const handleSetupApp = (): void => {
    setStatus(DevicePairingStatus.IDLE);
  };

  const isIntroStatus = status === DevicePairingStatus.INTRO;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md'>
        {/* Intro state has its own header layout, hide dialog header */}
        {!isIntroStatus && (
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Phone2Icon className='h-5 w-5 text-primary' />
              Add New Device
            </DialogTitle>
            <DialogDescription>Scan with your mobile device</DialogDescription>
          </DialogHeader>
        )}

        <div className='space-y-4'>
          {status === DevicePairingStatus.INTRO && (
            <PairingIntroState onSetupApp={handleSetupApp} onSkip={onClose} />
          )}

          {(status === DevicePairingStatus.IDLE ||
            status === DevicePairingStatus.STARTING) && <PairingLoadingState />}

          {status === DevicePairingStatus.ERROR && (
            <PairingErrorState error={error} onRetry={handleRetry} />
          )}

          {status === DevicePairingStatus.EXPIRED && (
            <PairingExpiredState onRetry={handleRetry} />
          )}

          {status === DevicePairingStatus.SUCCESS && <PairingSuccessState />}

          {status === DevicePairingStatus.ACTIVE && pairingData && (
            <PairingActiveState
              pairingData={pairingData}
              qrValue={buildPairingQrValue(pairingData.pairingCode, tenant)}
            />
          )}
        </div>

        {/* Only show footer with cancel button when not in intro state (intro has its own buttons) */}
        {!isIntroStatus && (
          <DialogFooter>
            <Button variant={ButtonVariants.SECONDARY} onClick={onClose}>
              {cancelButtonText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
